import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  getHomeworkSubmissionById,
  getAllTenantSubmissions,
  submitStudentHomework,
  evaluateStudentSubmission,
  adminUpdateSubmission,
} from "../../api/admin/homeworkSubmission.api";

/* ============================================================
   FETCH SINGLE SUBMISSION RECORD DETAIL BY ID
============================================================ */
export const fetchSubmissionById = createAsyncThunk(
  "admin/homeworkSubmission/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await getHomeworkSubmissionById(id);

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch Homework Submission details."
      );
    }
  }
);

/* ============================================================
   FETCH GLOBAL SUBMISSIONS CROSS-TENANT CONTEXT (UPDATED)
============================================================ */
export const fetchSubmissionsByAssignment = createAsyncThunk(
  "admin/homeworkSubmission/fetch",
  async (
    { page = 1, limit = 10, search = "", status } = {},
    { rejectWithValue }
  ) => {
    try {
      // Passes pure global structural queries directly through layout options maps
      const res = await getAllTenantSubmissions({
        params: {
          page,
          limit,
          search,
          status, // e.g. "pending", "submitted", "reviewed"
        }
      });

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      return res;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch Homework Submission Records."
      );
    }
  }
);

/* ============================================================
   STUDENT SUBMISSION ACTION DISPATCH
============================================================ */
export const executeStudentSubmission = createAsyncThunk(
  "admin/homeworkSubmission/submit",
  async (payload, { rejectWithValue }) => {
    try {
      console.log("📡 EXECUTE STUDENT SUBMISSION PAYLOAD:", payload);

      const res = await submitStudentHomework(payload);

      console.log("📥 EXECUTE STUDENT SUBMISSION RESPONSE:", res);

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      return res;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to submit Homework assignment data."
      );
    }
  }
);

/* ============================================================
   TEACHER EVALUATION & METRICS SYNCHRONIZATION
============================================================ */
export const recordSubmissionEvaluation = createAsyncThunk(
  "admin/homeworkSubmission/evaluate",
  async (
    {
      id,
      evaluationData,
    },
    { rejectWithValue }
  ) => {
    try {
      // Maps configuration payload structurally to expected flat model parameters
      const apiPayload = {
        student_homework_status_id: id,
        ...evaluationData,
      };

      console.log("📡 RECORD SUBMISSION EVALUATION PAYLOAD:", apiPayload);

      const res = await evaluateStudentSubmission(apiPayload);

      console.log("📥 RECORD SUBMISSION EVALUATION RESPONSE:", res);

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      return {
        id,
        updatedMetrics: evaluationData,
        message: res.message,
      };
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to record evaluation metrics."
      );
    }
  }
);

/* ============================================================
   ADMIN INTERVENTION & AUDIT NOTE OVERRIDE
============================================================ */
export const overrideSubmissionByAdmin = createAsyncThunk(
  "admin/homeworkSubmission/adminOverride",
  async (
    {
      id,
      adminOverrideData,
    },
    { rejectWithValue }
  ) => {
    try {
      // Maps configuration payload structurally to expected flat model parameters
      const apiPayload = {
        student_homework_status_id: id,
        ...adminOverrideData,
      };

      console.log("📡 OVERRIDE SUBMISSION BY ADMIN PAYLOAD:", apiPayload);

      const res = await adminUpdateSubmission(apiPayload);

      console.log("📥 OVERRIDE SUBMISSION BY ADMIN RESPONSE:", res);

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      return {
        id,
        updatedMetrics: adminOverrideData,
        message: res.message,
      };
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to override submission metrics via admin pipeline."
      );
    }
  }
);